<template>
  <div id="app">
    <!-- Global Loading State Indicator (development only) -->
    <!-- <div v-if="isDevelopment" 
         style="position: fixed; top: 10px; left: 10px; z-index: 9999; background: rgba(0,0,0,0.8); color: white; padding: 8px 12px; border-radius: 4px; font-size: 12px;">
      🔄 Global Loading: {{ isGlobalLoading ? 'TRUE' : 'FALSE' }}
    </div> -->
    
    <!-- Global notification component -->
    <Notifications />
    
    <!-- Enhancement Progress Modal -->
    <EnhancementProgressModal 
      :is-visible="isEnhancementModalVisible"
      :progress="enhancementProgress"
      :analysis-type="enhancementAnalysisType"
      :current-phase="enhancementCurrentPhase"
    />
    
    <!-- Main application layout based on authentication status and route type -->
    <template v-if="isLoggedIn && !isFullPage">
      <!-- Authenticated layout with global navigation -->
      <div class="authenticated-layout">
        <!-- Global Header -->
        <AppHeader 
          :current-workspace-name="currentWorkspaceName"
          :user-workspaces="userWorkspaces"
          :current-workspace-id="currentWorkspaceId"
          :is-switching-workspace="isSwitchingWorkspace"
          :current-user-role="currentUserRole"
          :user-plan="userPlan"
          :user-initials="userInitials"
          :user-avatar-url="userAvatarUrl"
          :is-menu-collapsed="isMenuCollapsed"
          @workspace-change="handleWorkspaceChange"
          @select-plan="handleSelectPlan"
        />
        
                 <!-- Global Sidebar -->
         <AppSidebar 
           :is-menu-collapsed="isMenuCollapsed"
           @toggle-menu="toggleMenu"
           @logout="logout"
         />
        
        <!-- Main Content Area -->
        <main class="main-content" :class="{ 'collapsed': isMenuCollapsed }">
          <!-- Automatic Page Layout Wrapper for all authenticated views -->
          <div class="page-container">
            <router-view />
          </div>
        </main>
      </div>
    </template>
    
    <!-- Non-authenticated layout (full page) or full-page routes -->
    <template v-else>
      <main class="full-page">
        <router-view />
      </main>
    </template>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Notifications from '@/components/common/Notifications.vue';
import AppSidebar from '@/components/common/AppSidebar.vue';
import AppHeader from '@/components/common/AppHeader.vue';
import EnhancementProgressModal from '@/components/modals/EnhancementProgressModal.vue';
import { useActivityTracker } from '@/composables/useActivityTracker';
import { useGlobalLoading } from '@/composables/useGlobalLoading';
import { useEnhancementProgress } from '@/composables/useEnhancementProgress';
import { usePerformanceTracking } from '@/utils/performanceMonitor';

export default {
  name: 'App',
  components: {
    Notifications,
    AppSidebar,
    AppHeader,
    EnhancementProgressModal
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    
    // Initialize activity tracker
    useActivityTracker();
    
    // Global loading state
    const { isLoading: isGlobalLoading } = useGlobalLoading();
    
    // Enhancement progress state
    const { isModalVisible: isEnhancementModalVisible, progress: enhancementProgress, analysisType: enhancementAnalysisType, currentPhase: enhancementCurrentPhase } = useEnhancementProgress();
    
    // Performance tracking (development only)
    const { startMemoryTracking } = usePerformanceTracking('App');
    if (process.env.NODE_ENV === 'development') {
      startMemoryTracking();
    }
    
    // Development mode check
    const isDevelopment = computed(() => process.env.NODE_ENV === 'development');
    
         // Reactive data for navigation
     const isMenuCollapsed = ref(false);
    
    // Workspace and user data - use computed properties from store
    const currentWorkspaceName = computed(() => store.getters['workspace/currentWorkspaceName'] || 'My Workspace');
    const userWorkspaces = computed(() => store.getters['workspace/userWorkspaces'] || []);
    const currentWorkspaceId = computed(() => String(store.getters['workspace/currentWorkspaceId'] || ''));
    const isSwitchingWorkspace = computed(() => store.getters['workspace/getLoadingState']('switching') || false);
    const currentUserRole = computed(() => store.getters['workspace/currentUserRole'] || 'MEMBER');
    const userPlan = ref('FREE');
    
    // User profile data for avatar display
    const userProfile = computed(() => store.getters['user/profile']);
    const userInitials = computed(() => {
      // First try to get initials from user profile (if available)
      if (userProfile.value?.initials) {
        return userProfile.value.initials;
      }
      
      // Then try to calculate from user profile first/last name
      if (userProfile.value?.firstName && userProfile.value?.lastName) {
        return `${userProfile.value.firstName.charAt(0)}${userProfile.value.lastName.charAt(0)}`.toUpperCase();
      }
      
      // Fallback to auth store user data if profile not loaded
      const authUser = store.getters['auth/getUser'];
      if (authUser?.firstName && authUser?.lastName) {
        return `${authUser.firstName.charAt(0)}${authUser.lastName.charAt(0)}`.toUpperCase();
      }
      
      return 'U'; // Ultimate fallback
    });
    const userAvatarUrl = computed(() => userProfile.value?.avatarUrl || null);
    
    // Computed properties
    const isLoggedIn = computed(() => store.getters['auth/isLoggedIn']);

    const isFullPage = computed(() => {
      // Routes that should be displayed in full-page mode
      const fullPageRoutes = ['Login', 'Register', 'VerifyEmail', 
                             'ResetPasswordRequest', 'ResetPassword', 'NotFound',
                             'DeletionConfirm', 'DeletionSent', 'DeletionExpired'];
      return fullPageRoutes.includes(route.name);
    });
    
         // Navigation methods
     const toggleMenu = () => {
       isMenuCollapsed.value = !isMenuCollapsed.value;
     };
    
    // Workspace methods
    const handleWorkspaceChange = async (workspaceId) => {
      try {
        // Use the store action to switch workspace
        await store.dispatch('workspace/setCurrentWorkspace', String(workspaceId));
      } catch (error) {
        console.error('Error switching workspace:', error);
      }
    };
    
    const handleSelectPlan = () => {
      // Navigate to My Account page with plans tab
      router.push('/my-account?tab=plans');
    };
    
    // Methods
    const logout = async () => {
      try {
        await store.dispatch('auth/logout');
        // Redirect to login page with logout message
        router.push('/login?message=logged-out');
      } catch (error) {
        console.error('Logout error:', error);
      }
    };
    
    return {
      // Global loading state
      isGlobalLoading,
      isDevelopment,
      
      // Enhancement modal state
      isEnhancementModalVisible,
      enhancementProgress,
      enhancementAnalysisType,
      enhancementCurrentPhase,
      
             // Navigation state
       isMenuCollapsed,
      
      // Workspace and user data
      currentWorkspaceName,
      userWorkspaces,
      currentWorkspaceId,
      isSwitchingWorkspace,
      currentUserRole,
      userPlan,
      userInitials,
      userAvatarUrl,
      
      // Computed
      isLoggedIn,
      isFullPage,
      
             // Methods
       toggleMenu,
      handleWorkspaceChange,
      handleSelectPlan,
      logout
    };
  }
};
</script>

<style>
/* Base styles */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: Arial, sans-serif;
  background-color: #f5f7fa;
  color: #333;
  line-height: 1.6;
}

.container {
  width: 100%;
  padding: 0 20px;
}

/* App layout */
#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Authenticated layout */
.authenticated-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f9fafb;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-left: 256px; /* Account for fixed sidebar width */
  margin-top: 64px; /* Account for fixed header height */
  transition: margin-left 0.3s ease;
  /* Padding moved to .page-container for automatic inheritance */
}

.main-content.collapsed {
  margin-left: 80px; /* Account for collapsed sidebar width + buffer */
}

/* Responsive adjustments for authenticated layout */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0 !important;
    margin-top: 64px;
    /* Padding moved to .page-container for automatic inheritance */
  }
}



main {
  flex: 1;
}

main.full-page {
  display: flex;
  min-height: 100vh;
}

/* Center content for specific full-page routes that need centering */
main.full-page:has(.deletion-confirm-page),
main.full-page:has(.deletion-sent-page),
main.full-page:has(.deletion-expired-page) {
  align-items: center;
  justify-content: center;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .nav-menu {
    gap: 12px;
  }
}
</style>